import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { map, take, switchMap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

/**
 * Guard que protege rutas de sistema (/admin-sistema/*).
 * Verifica únicamente que el usuario pertenezca a la empresa PROVIDER.
 *
 * La verificación de permisos específicos de módulo (canView, canCreate, etc.)
 * es responsabilidad exclusiva de permissionGuard, que se aplica en cada
 * ruta hija. Esta separación de responsabilidades garantiza que:
 *   - adminGuard: «Perteneces a la empresa Provider?» (control por empresa)
 *   - permissionGuard: «Tienes permiso para este módulo?» (control por permiso)
 *
 * @example
 * // Ruta padre gateada por adminGuard (verifica empresa Provider)
 * { path: 'admin-sistema', canActivate: [adminGuard], children: [
 *   // Rutas hijas gateadas por permissionGuard (verifica permiso de módulo)
 *   { path: 'roles', component: RolesComponent, canActivate: [permissionGuard] }
 * ]}
 */
export const adminGuard: CanActivateFn = (route, state): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const usuarioService = inject(UsuarioService);
  const router = inject(Router);

  const perfil = usuarioService.perfil();

  if (!perfil?.role?.id || !perfil?.role?.name) {
    return usuarioService.getPerfil().pipe(
      take(1),
      switchMap((perfilData) => {
        if (!perfilData?.role?.name) {
          return of(router.createUrlTree(['/404'], { queryParams: { reason: 'unauthorized' } }));
        }
        return of(isProviderScope(perfilData, router));
      }),
      catchError(() =>
        of(router.createUrlTree(['/404'], { queryParams: { reason: 'unauthorized' } }))
      )
    );
  }

  return isProviderScope(perfil, router);
};

/**
 * Verifica si el usuario pertenece a la empresa PROVIDER (cualquier rol).
 * Si no, redirige a /404 con reason=unauthorized.
 */
function isProviderScope(perfil: any, router: Router): boolean | UrlTree {
  const companyType = perfil?.companyType ?? '';

  if (companyType === 'PROVIDER') {
    return true;
  }
  return router.createUrlTree(['/404'], { queryParams: { reason: 'unauthorized' } });
}

/**
 * @deprecated Se mantiene por compatibilidad pero ya no hace nada.
 * El caché de permisos se maneja en PermissionService mediante signals.
 */
export function clearAdminPermissionCache(_roleId?: number): void {}

